import { bcEnums } from 'common/common/constants/boldchat-enums';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { snakeCase } from 'lodash';
import { OperatorStatusService } from 'shared/services/operator-status/operator-status.service';
import { BcSettingsService } from 'common/settings/settings.service';
import { BcConfigService } from 'common/config/provider';
import { ServiceAvailabilityCollectorService } from 'shared/services/service-availability-collector/service-availability-collector-service';

@Component({
  selector: 'app-operator-status-button',
  templateUrl: './operator-status-button.component.html',
  styleUrls: ['./operator-status-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OperatorStatusButtonComponent implements OnInit, OnDestroy {
  readonly statusTypes = bcEnums.statuses;
  readonly bcEnums = bcEnums;
  readonly additionalChannelServiceType = bcEnums.services.ASYNC_MESSAGE;

  @Input()
  serviceType: number;
  @Input()
  isSmsDisabled: boolean;
  @Output()
  readonly changeStatus = new EventEmitter<void>();

  statusList = [];
  isServiceLocked = false;
  selectedStatus?: any;
  clientServicesSubscription: Subscription;
  additionalChannels = [];
  channelsIncludedInOmniQueue = [];
  shouldDisplayAdditionalChannels = false;

  constructor(private readonly _operatorStatusService: OperatorStatusService,
              private readonly _bcConfig: BcConfigService,
              private readonly _settingsService: BcSettingsService,
              private readonly _elRef: ElementRef,
              private readonly _changeDetector: ChangeDetectorRef,
              private readonly _serviceAvailabilityCollectorService: ServiceAvailabilityCollectorService) {
  }

  ngOnInit(): void {
    if (this._bcConfig && this._bcConfig.current.features.operatorStatusDropdown) {
      this.clientServicesSubscription = this._subscribeCurrentStatuses();
      this._getCustomStatuses();
    }
    this._settingsService.getSettings().then(settings => {
      this.additionalChannels = settings.textChat.asyncMessage.allowedAsyncMessageProviders.map(providers => providers.label);
      if (this.isOmniQueue && this._isOmniQueueEnabled(settings)) {
        this._collectChannelsIncludedInOmniQueue(settings);
        this._changeDetector.detectChanges();
        this._setOmniListHeaderHeight();
      }
    });
  }

  ngOnDestroy(): void {
    if (this._bcConfig && this._bcConfig.current.features.operatorStatusDropdown) {
      this.clientServicesSubscription.unsubscribe();
    }
  }

  get isOmniQueue(): boolean {
    return this.serviceType === this.bcEnums.services.OMNI;
  }

  get serviceTranslationKey(): string {
    return `bcOperatorStatusButton.service.${this.serviceKey}`;
  }

  get buttonIconByService(): string {
    switch (this.serviceType) {
      case this.bcEnums.services.CHATS:
        return 'chat-large';
      case this.bcEnums.services.EMAILS:
        return 'mail';
      case this.bcEnums.services.SMS:
        return this.isSmsDisabled ? 'alert' : 'sms';
      case this.bcEnums.services.FACEBOOK:
        return 'message';
      case this.bcEnums.services.ASYNC_MESSAGE:
        return 'async-message';
      case this.bcEnums.services.OMNI:
        return 'person-filled';
    }
  }

  get serviceClass(): string {
    const statusKey = this._operatorStatusService.getStatusKey(this.serviceType);
    return `${this.serviceKey}-${statusKey}`;
  }

  get serviceKey(): string {
    return this._operatorStatusService.getServiceKey(this.serviceType);
  }

  get isLocked(): boolean {
    return this._operatorStatusService.isLockedStatus(this.serviceType);
  }

  get isCustomStatus(): boolean {
    return this._operatorStatusService.isCustomStatus(this.serviceType);
  }

  get hasDropdown(): boolean {
    const statusType = this._operatorStatusService.getStatusType(this.serviceType);
    return statusType !== this.bcEnums.statuses.OFFLINE && (this.serviceType !== this.bcEnums.services.SMS || !this.isSmsDisabled);
  }

  get customStatusName(): string {
    return this._operatorStatusService.getCustomStatusName(this.serviceType, this.isSmsDisabled);
  }

  setStatus(status): void {
    if (status === this.selectedStatus) {
      return;
    }

    if (this._isStatusCustom(status)) {
      this._setCustomStatus(status.id);
    } else {
      this._setBuiltinStatus(status.statusType);
    }
  }

  private async _getCustomStatuses(): Promise<void> {
    const key = snakeCase(this.serviceKey);
    const statuses = await this._operatorStatusService.getAllAvailabilities();
    this.statusList = statuses.filter((status) => status.supportedServices[key]);

    const available = this._getBuiltinStatus('bcOperatorStatusButton.available', this.bcEnums.statuses.AVAILABLE);
    const away = this._getBuiltinStatus('bcOperatorStatusButton.away', this.bcEnums.statuses.AWAY);

    this._extendServiceStatusList(available, away);
    this._changeDetector.detectChanges();
  }

  private _setCustomStatus(customStatusId): void {
    this._operatorStatusService.setCustomStatus(customStatusId, this.serviceType);
  }

  private _setBuiltinStatus(statusType): void {
    switch (statusType) {
      case this.bcEnums.statuses.AVAILABLE:
        this._setStatusToAvailable();
        break;
      case this.bcEnums.statuses.AWAY:
        this._setStatusToAway();
        break;
      default:
        throw Error('Invalid status type');
    }
  }

  private _setStatusToAvailable(): void {
    this._operatorStatusService.changeStatusToAvailable(this.serviceType);
  }

  private _setStatusToAway(): void {
    this._operatorStatusService.changeStatusToAway(this.serviceType);
  }

  private _extendServiceStatusList(builtinAvailable, builtinAway): void {
    this.statusList = [
      ...this.statusList,
      builtinAvailable,
      builtinAway
    ];
    this.statusList.sort((a, b) => this._statusCompareByType(a, b));
  }

  private _subscribeCurrentStatuses(): Subscription {
    return this._operatorStatusService.observeClientStatuses().subscribe(entries => this._onCurrentStatusChange(entries));
  }

  private _onCurrentStatusChange(entries): void {
    entries.forEach(entry => {
      if (parseInt(entry.ServiceTypeID, 10) === this.serviceType) {
        this.isServiceLocked = (entry.CustomOperatorStatusID && entry.OperatorCanChangeStatus !== 1);
        this.statusList.forEach(status => {
          if (entry.CustomOperatorStatusID === status.id && entry.StatusType === status.statusType) {
            this.selectedStatus = status;
          }
        });
      }
    });
    this._changeDetector.detectChanges();
  }

  private _statusCompareByType(a, b): number {
    if (a.statusType === b.statusType) {
      return this._statusCompareByBuiltinFirst(a, b);
    }
    if (a.statusType < b.statusType) {
      return 1;
    }
    return -1;
  }

  private _statusCompareByBuiltinFirst(a, b): number {
    if ((!a.id && !b.id) || (a.id && b.id)) {
      return this._statusCompareByName(a, b);
    }
    return !a.id ? -1 : 1;
  }

  private _statusCompareByName(a: { name: string }, b: { name: string }): number {
    return a.name.localeCompare(b.name);
  }

  private _getBuiltinStatus(name: string, statusType) {
    return {
      id: null,
      name,
      statusType,
      operatorCanChange: true
    };
  }

  private _isStatusCustom(status): boolean {
    return !!status.id;
  }

  private _setOmniListHeaderHeight(): void {
    const omniListHeader = this._elRef.nativeElement.querySelector('.list-header-omni-queue');
    if (omniListHeader) {
      const omniListHeaderInfo = omniListHeader.getBoundingClientRect();
      if (omniListHeaderInfo.height > 50) {
        omniListHeader.classList.add('small-font');
      }
    }
  }

  private _isOmniQueueEnabled(settings): boolean {
    return this._bcConfig.current.features.omniQueue && settings.omniQueueSettings && settings.omniQueueSettings.includedServiceTypes;
  }

  private _collectChannelsIncludedInOmniQueue(settings): void {
    this.channelsIncludedInOmniQueue = [];

    settings.omniQueueSettings.includedServiceTypes.forEach(service => {
      if (this._serviceAvailabilityCollectorService.isServiceEnabled(service)) {
        switch (service) {
          case bcEnums.services.CHATS:
          case bcEnums.services.EMAILS:
          case bcEnums.services.SMS:
          case bcEnums.services.FACEBOOK:
            this.channelsIncludedInOmniQueue.push('bcOperatorStatusButton.service.' + bcEnums.serviceNames[service]);
            break;
          case bcEnums.services.ASYNC_MESSAGE:
            this.shouldDisplayAdditionalChannels = true;
            break;
        }
      }
    });
  }
}
